import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Palette, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Frame,
  Brush,
  Image,
  Layers,
  PenTool,
  Home,
  Building,
  Gift,
  User,
  Camera,
  Printer,
  Scissors,
  Gem,
  Flame,
  Music,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

// Art categories/types
const ART_TYPES = [
  { value: 'pittura', label: 'Pittura / Dipinto', icon: Brush },
  { value: 'scultura', label: 'Scultura', icon: Gem },
  { value: 'ceramica', label: 'Ceramica artistica', icon: Flame },
  { value: 'illustrazione', label: 'Illustrazione', icon: PenTool },
  { value: 'fotografia', label: 'Fotografia artistica', icon: Camera },
  { value: 'grafica', label: 'Grafica / Stampa d\'arte', icon: Printer },
  { value: 'calligrafia', label: 'Calligrafia', icon: BookOpen },
  { value: 'collage', label: 'Collage / Mixed media', icon: Layers },
  { value: 'incisione', label: 'Incisione / Acquaforte', icon: Scissors },
  { value: 'ritratto', label: 'Ritratto', icon: User },
  { value: 'murale', label: 'Murale / Street art', icon: Building },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Technique/style preferences
const TECHNIQUES = [
  { value: 'olio', label: 'Olio su tela', icon: Brush },
  { value: 'acrilico', label: 'Acrilico', icon: Brush },
  { value: 'acquerello', label: 'Acquerello', icon: Brush },
  { value: 'pastello', label: 'Pastello / Carboncino', icon: PenTool },
  { value: 'digitale', label: 'Arte digitale', icon: Image },
  { value: 'terracotta', label: 'Terracotta / Ceramica', icon: Flame },
  { value: 'bronzo', label: 'Bronzo / Metallo', icon: Gem },
  { value: 'marmo', label: 'Marmo / Pietra', icon: Gem },
  { value: 'legno', label: 'Legno', icon: Frame },
  { value: 'vetro', label: 'Vetro artistico', icon: Sparkles },
  { value: 'tecnica_mista', label: 'Tecnica mista', icon: Layers },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Purpose/Destination
const ART_PURPOSES = [
  { value: 'casa_soggiorno', label: 'Per il soggiorno', icon: Home },
  { value: 'casa_camera', label: 'Per la camera da letto', icon: Home },
  { value: 'ufficio', label: 'Per l\'ufficio / Studio', icon: Building },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'collezione', label: 'Per la mia collezione', icon: Frame },
  { value: 'giardino', label: 'Per giardino / Esterno', icon: Sparkles },
  { value: 'ristorante', label: 'Per ristorante / Hotel / Locale', icon: Building },
  { value: 'evento', label: 'Per un evento speciale', icon: Star },
  { value: 'commemorativo', label: 'Opera commemorativa', icon: Heart },
  { value: 'altro', label: 'Altro', icon: Palette },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'piccolo', label: 'Piccolo (fino a 30 cm)', description: 'Oggetti da tavolo, miniature' },
  { value: 'medio', label: 'Medio (30-60 cm)', description: 'Opere da parete standard' },
  { value: 'grande', label: 'Grande (60-120 cm)', description: 'Statement piece' },
  { value: 'monumentale', label: 'Monumentale (oltre 120 cm)', description: 'Installazioni, murali' },
  { value: 'personalizzato', label: 'Misure personalizzate', description: 'Su indicazione' },
  { value: 'non_so', label: 'Non so ancora', description: 'Vorrei consiglio' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '100-300', label: '€100 - €300', description: 'Piccole opere' },
  { value: '300-600', label: '€300 - €600', description: 'Opere medie' },
  { value: '600-1000', label: '€600 - €1.000', description: 'Opere importanti' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Commissioni elaborate' },
  { value: '2000-5000', label: '€2.000 - €5.000', description: 'Grandi formati / Sculture' },
  { value: '5000+', label: '€5.000+', description: 'Opere d\'autore / Installazioni' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Urgente' },
  { value: '1month', label: '1 Mese', description: 'Prioritario' },
  { value: '2months', label: '2 Mesi', description: 'Standard' },
  { value: '3months', label: '3+ Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example artworks
const ART_EXAMPLES = [
  {
    idea: "Ritratto di famiglia in stile impressionista",
    result: "Olio su tela 80x100cm con 5 soggetti, colori caldi e pennellate vibranti",
    image: "https://images.unsplash.com/photo-1748723940982-c355793626af?w=400&h=300&fit=crop",
    category: "Pittura"
  },
  {
    idea: "Scultura astratta per giardino",
    result: "Opera in metallo 90cm di altezza, forme organiche ispirate alla natura",
    image: "https://images.unsplash.com/photo-1781422921703-182cdbc2b795?w=400&h=300&fit=crop",
    category: "Scultura"
  },
  {
    idea: "Set di ceramiche artistiche per ristorante",
    result: "12 piatti decorativi dipinti a mano con motivi mediterranei",
    image: "https://images.unsplash.com/photo-1762926627840-6caf5516145e?w=400&h=300&fit=crop",
    category: "Ceramica"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho commissionato una scultura per il mio giardino. L'artista ha interpretato perfettamente la mia visione. Opera unica che tutti ammirano.",
    author: "Roberto P.",
    location: "Verona"
  },
  {
    text: "Cercavo un set di ceramiche artistiche per il mio ristorante. Qualità eccezionale e design esclusivo che caratterizza il locale.",
    author: "Chef Antonio M.",
    location: "Napoli"
  },
  {
    text: "Un ritratto commissionato per i miei genitori. L'artista ha catturato non solo le sembianze ma anche l'anima. Regalo perfetto.",
    author: "Lucia F.",
    location: "Firenze"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Che tipo di opere posso commissionare?",
    answer: "Puoi richiedere qualsiasi tipo di opera d'arte: dipinti (olio, acrilico, acquerello), sculture (bronzo, marmo, legno, ceramica), ceramiche artistiche, illustrazioni, fotografia artistica, calligrafia, murali e molto altro."
  },
  {
    question: "È gratuito inviare una richiesta?",
    answer: "Sì, è completamente gratuito. Descrivi l'opera che desideri e ricevi proposte da artisti senza alcun costo o impegno."
  },
  {
    question: "Posso vedere bozzetti o prototipi prima?",
    answer: "Certamente! La maggior parte degli artisti propone schizzi preliminari, rendering 3D per sculture, o campioni per ceramiche prima di procedere con l'opera finale."
  },
  {
    question: "Quanto tempo ci vuole per realizzare un'opera?",
    answer: "Dipende dalla tipologia e complessità. Un dipinto medio richiede 2-4 settimane, una scultura in bronzo può richiedere 2-4 mesi, ceramiche complesse 1-2 mesi."
  },
  {
    question: "Come funziona il pagamento?",
    answer: "Generalmente si versa un acconto alla conferma (30-50%) e il saldo alla consegna. Le modalità precise vengono concordate con l'artista scelto."
  }
];

export default function ArteRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    art_type: '',
    technique: '',
    purpose: '',
    size: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Arte su Commissione | Artisti Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `art-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi l\'opera che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_art_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const artTypeLabel = ART_TYPES.find(t => t.value === formData.art_type)?.label || '';
      const techniqueLabel = TECHNIQUES.find(t => t.value === formData.technique)?.label || '';
      const purposeLabel = ART_PURPOSES.find(p => p.value === formData.purpose)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(s => s.value === formData.size)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (artTypeLabel) details.push(`🎨  Tipo opera: ${artTypeLabel}`);
      if (techniqueLabel) details.push(`🖌️  Tecnica/Materiale: ${techniqueLabel}`);
      if (purposeLabel) details.push(`🏛️  Destinazione: ${purposeLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${artTypeLabel} - ${techniqueLabel}`.trim() || 'Opera d\'arte su commissione',
        description: fullDescription,
        category: 'Arte',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artisti.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('art-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Arte su Commissione | Dipinti, Sculture, Ceramiche da Artisti Italiani | Rarifex</title>
        <meta name="description" content="Commissiona un'opera d'arte unica da artisti italiani. Dipinti, sculture, ceramiche, illustrazioni, fotografia artistica. Descrivi la tua idea e ricevi proposte personalizzate. Gratuito e senza impegno." />
        <meta name="keywords" content="arte su commissione, opera d'arte personalizzata, artista italiano, dipinto su misura, scultura su commissione, ceramica artistica, illustrazione personalizzata, arte Made in Italy" />
        <link rel="canonical" href="https://www.rarifex.com/arte-su-commissione" />
        <meta property="og:title" content="Arte su Commissione | Artisti Italiani | Rarifex" />
        <meta property="og:description" content="Commissiona un'opera d'arte unica da artisti italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/arte-su-commissione" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-violet-50/50 via-white to-fuchsia-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Palette className="w-4 h-4" />
                    Arte su Commissione
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    L'opera d'arte{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-fuchsia-600">
                      che hai sempre sognato
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Artisti italiani la creano apposta per te.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Dipinti, sculture, ceramiche, illustrazioni: descrivi la tua idea e ricevi proposte personalizzate da artisti e artigiani esperti.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artisti professionisti</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-violet-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <Card id="art-form" className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-slate-800">Descrivi la tua opera</h2>
                      <p className="text-slate-500 text-sm mt-1">Ricevi proposte da artisti italiani</p>
                    </div>
                    
                    {/* Title */}
                    <div>
                      <Label className="text-slate-700">Titolo progetto (opzionale)</Label>
                      <Input
                        placeholder="Es: Ritratto di famiglia, Scultura per giardino..."
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Description */}
                    <div>
                      <Label className="text-slate-700">Descrivi l'opera che desideri *</Label>
                      <Textarea
                        placeholder="Descrivi l'opera che immagini: soggetto, stile, colori, dimensioni approssimative, dove verrà posizionata... Più dettagli fornisci, più precise saranno le proposte."
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="mt-1 min-h-[120px]"
                        required
                      />
                    </div>
                    
                    {/* Art Type */}
                    <div>
                      <Label className="text-slate-700">Tipo di opera</Label>
                      <select
                        value={formData.art_type}
                        onChange={(e) => handleInputChange('art_type', e.target.value)}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      >
                        <option value="">Seleziona...</option>
                        {ART_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Technique */}
                    <div>
                      <Label className="text-slate-700">Tecnica / Materiale preferito</Label>
                      <select
                        value={formData.technique}
                        onChange={(e) => handleInputChange('technique', e.target.value)}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      >
                        <option value="">Seleziona...</option>
                        {TECHNIQUES.map(tech => (
                          <option key={tech.value} value={tech.value}>{tech.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Purpose */}
                    <div>
                      <Label className="text-slate-700">Destinazione</Label>
                      <select
                        value={formData.purpose}
                        onChange={(e) => handleInputChange('purpose', e.target.value)}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      >
                        <option value="">Seleziona...</option>
                        {ART_PURPOSES.map(purpose => (
                          <option key={purpose.value} value={purpose.value}>{purpose.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Size & Budget Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Dimensioni</Label>
                        <select
                          value={formData.size}
                          onChange={(e) => handleInputChange('size', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                        >
                          <option value="">Seleziona...</option>
                          {SIZE_OPTIONS.map(size => (
                            <option key={size.value} value={size.value}>{size.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Budget</Label>
                        <select
                          value={formData.budget}
                          onChange={(e) => handleInputChange('budget', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                        >
                          <option value="">Seleziona...</option>
                          {BUDGET_OPTIONS.map(budget => (
                            <option key={budget.value} value={budget.value}>{budget.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Timeline */}
                    <div>
                      <Label className="text-slate-700">Tempistiche</Label>
                      <select
                        value={formData.timeline}
                        onChange={(e) => handleInputChange('timeline', e.target.value)}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      >
                        <option value="">Seleziona...</option>
                        {TIMELINE_OPTIONS.map(timeline => (
                          <option key={timeline.value} value={timeline.value}>{timeline.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Image Upload */}
                    <div>
                      <Label className="text-slate-700">Immagini di riferimento (opzionale)</Label>
                      <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-violet-300 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                          id="art-images"
                          disabled={uploadingFile}
                        />
                        <label htmlFor="art-images" className="cursor-pointer flex flex-col items-center gap-2">
                          {uploadingFile ? (
                            <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                          ) : (
                            <Upload className="w-8 h-8 text-slate-400" />
                          )}
                          <span className="text-sm text-slate-500">
                            {uploadingFile ? 'Caricamento...' : 'Clicca per caricare (max 5 immagini)'}
                          </span>
                        </label>
                      </div>
                      
                      {/* Uploaded images preview */}
                      {formData.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.attachments.map((url, index) => (
                            <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden group">
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              >
                                <X className="w-5 h-5 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-5 h-5 mr-2" />
                      )}
                      {loading ? 'Invio in corso...' : 'Ricevi proposte gratis'}
                    </Button>
                    
                    <p className="text-xs text-slate-400 text-center">
                      Gratuito e senza impegno. Riceverai proposte entro 24-48 ore.
                    </p>
                  </form>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come funziona
              </h2>
              <p className="text-lg text-slate-600">
                Dall'idea all'opera d'arte in 4 semplici passi
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-8">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Racconta l\'opera che desideri: soggetto, stile, dimensioni' },
                { icon: Users, title: 'Ricevi', desc: 'Gli artisti interessati ti inviano le loro proposte' },
                { icon: CheckCircle, title: 'Scegli', desc: 'Valuta portfolio, preventivi e scegli l\'artista ideale' },
                { icon: Heart, title: 'Ammira', desc: 'Segui la creazione e ricevi la tua opera unica' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg">
                    <step.icon className="w-8 h-8" />
                  </div>
                  <div className="text-sm font-semibold text-violet-600 mb-1">Passo {index + 1}</div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Esempi di commissioni realizzate
              </h2>
              <p className="text-lg text-slate-600">
                Da un'idea a un'opera d'arte unica
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {ART_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-[4/3] relative">
                    <img src={example.image} alt={example.category} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-violet-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {example.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="mb-3">
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">L'idea</div>
                      <p className="text-slate-700 font-medium">{example.idea}</p>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Il risultato</div>
                      <p className="text-slate-600 text-sm">{example.result}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa dicono i nostri clienti
              </h2>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6 bg-gradient-to-br from-violet-50 to-fuchsia-50 border-0">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-200 flex items-center justify-center text-violet-700 font-bold">
                      {testimonial.author[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">{testimonial.author}</div>
                      <div className="text-sm text-slate-500">{testimonial.location}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Domande frequenti
                </h2>
              </div>
              
              <div className="space-y-3">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index} 
                    className={`overflow-hidden transition-all cursor-pointer ${expandedFaq === index ? 'ring-2 ring-violet-200' : ''}`}
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-4 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-800 pr-4">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-4 pb-4 text-slate-600 border-t border-slate-100 pt-3">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-violet-600 to-fuchsia-600">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <Palette className="w-16 h-16 mx-auto mb-6 text-white/90" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Pronto a commissionare la tua opera d'arte?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Descrivi la tua idea e ricevi proposte da artisti italiani. Gratuito e senza impegno.
              </p>
              <Button 
                size="lg" 
                className="bg-white text-violet-600 hover:bg-violet-50 font-semibold text-lg px-8 py-6 h-auto shadow-xl"
                onClick={scrollToForm}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Inizia ora
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-800 mb-2">Accedi per inviare la richiesta</h3>
            <p className="text-slate-600 mb-6">
              Per ricevere proposte dagli artisti devi accedere o creare un account gratuito.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAuthPrompt(false)}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-violet-600 hover:bg-violet-700"
                onClick={() => navigate('/login?redirect=/arte-su-commissione')}
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
